/*
 * Copyright (c) 2025 CalAmp Corp.  All Rights Reserved
 */
import {useEffect} from 'react';

/**
 * Props for the {@link MapContextMenuLayer} component.
 */
export interface MapContextMenuLayerProps {
  view?: __esri.MapView;
  onContextMenuOpen?: (payload: {
    visibility: boolean;
    x: number;
    y: number;
    mapPoint: __esri.Point | null;
  }) => void;
};

/**
 * The initial state for context menu click events.
 */
export const INITIAL_CLICK_STATE = {
  visibility : false,
  x          : 0,
  y          : 0,
  mapPoint   : null
};

/**
 * Provides a reliable context menu event layer for Esri MapView.
 * This component attaches native context menu and click event listeners to the map container,
 * enabling consistent right-click (context menu) and left-click (close menu) behavior.
 *
 * ```typescript
 * import {MapContextMenuLayer} from './MapContextMenuLayer';
 *
 * <MapContextMenuLayer
 *   view={mapView}
 *   onContextMenuOpen={({ visibility, x, y, mapPoint }) => {
 *     // Handle context menu open/close and position
 *   }}
 * />
 * ```
 *
 * ✨ Features:
 * - Attaches native `contextmenu` and `click` listeners to the Esri MapView container
 * - Converts screen coordinates to map points for context menu positioning
 * - Ensures only one set of listeners is attached at a time
 * - Cleans up listeners on unmount or prop change
 */
export const MapContextMenuLayer = ({
  view,
  onContextMenuOpen
}: MapContextMenuLayerProps) => {
  useEffect(() => {
    const container = view?.container as HTMLElement | undefined;
    let detach: () => void = () => undefined;

    if (view && container && onContextMenuOpen) {
      /**
       * Handles the native contextmenu event.
       * Prevents the default browser menu, stops propagation, and
       * invokes the callback with the menu position and map point.
       */
      const handleContextMenu = (e: MouseEvent) => {
        e.preventDefault();
        e.stopPropagation();

        const rect = container.getBoundingClientRect();

        const x = e.clientX - rect.left;
        const y = e.clientY - rect.top;

        const screenPoint = {x, y};

        const mapPoint = view.toMap(screenPoint);

        onContextMenuOpen({
          visibility : true,
          x          : e.clientX,
          y          : e.clientY,
          mapPoint
        });
      };

      const handleClick = () => {
        onContextMenuOpen(INITIAL_CLICK_STATE);
      };

      // the handlers are this run's own, so only one set is ever attached -
      // the previous run's set is detached by its cleanup before this one
      // attaches, on unmount or when the view or callback changes ...
      container.addEventListener('contextmenu', handleContextMenu);
      container.addEventListener('click', handleClick);
      detach = () => {
        container.removeEventListener('contextmenu', handleContextMenu);
        container.removeEventListener('click', handleClick);
      };
    }

    return () => {
      detach();
    };
  }, [view, onContextMenuOpen]);

  return null;
};

export default MapContextMenuLayer;
